<script setup>
import { cn } from '@/lib/utils';
import { SliderRange, SliderRoot, SliderThumb, SliderTrack, useForwardPropsEmits } from 'radix-vue';
import { computed } from 'vue';

const props = defineProps({
  name: { type: String, required: false },
  label: { type: Boolean, default: false },
  title: { type: String, required: false },
  defaultValue: { type: Array, required: false },
  modelValue: { type: Array, required: false },
  disabled: { type: Boolean, required: false },
  orientation: { type: String, required: false },
  dir: { type: String, required: false },
  inverted: { type: Boolean, required: false },
  min: { type: Number, required: false },
  max: { type: Number, required: false },
  step: { type: Number, required: false },
  minStepsBetweenThumbs: { type: Number, required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  class: { type: null, required: false },
});
const emits = defineEmits(['update:modelValue', 'valueCommit']);

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props;
  const { modelValue } = delegated;
  return { ...delegated, modelValue };
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
  <SliderRoot
    :class="
      cn(
        'relative flex w-full touch-none select-none items-center data-[orientation=vertical]:flex-col data-[orientation=vertical]:w-1 data-[orientation=vertical]:h-full',
        props.class
      )
    "
    v-bind="forwarded"
  >
    <SliderTrack
      class="relative h-1.5 w-full data-[orientation=vertical]:w-1.5 grow overflow-hidden rounded-full bg-slate-100/80 backdrop-blur-sm"
    >
      <SliderRange
        class="absolute h-full data-[orientation=vertical]:w-full bg-slate-900 rounded-full transition-transform"
      />
      <slot />
      <div
        v-if="label"
        class="absolute z-10 text-xs inset-x-2 -top-8 flex items-center justify-between pointer-events-none"
      >
        <span class="text-slate-600 font-medium">{{ title }}</span>
        <span
          class="font-mono text-xs text-slate-900 bg-white/90 px-2 py-1 rounded-md shadow-sm border border-slate-200/50"
          >{{ modelValue[0] }}</span
        >
      </div>
    </SliderTrack>
    <SliderThumb
      v-for="(_, key) in modelValue"
      :key="key"
      class="block h-4 w-4 rounded-full bg-white border border-slate-200 shadow-sm ring-offset-background transition-transform hover:scale-105 hover:shadow-md focus:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[dragging]:scale-105 data-[dragging]:shadow-md"
    />
  </SliderRoot>
</template>
